import React, { useCallback, useState } from 'react'
import XMLParser from 'react-xml-parser'

import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import I18n, { useT } from '@galo-monitor/i18n'
import Button from '@galo-monitor/web-components/Button'
import InputFile from '@galo-monitor/web-components/InputFile'
import Modal from '@galo-monitor/web-components/Modal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'

const ImportMapModal = (props) => {
  const {
    id,
    type,
    open,
    handleClose,
    onSuccess
  } = props

  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState('')

  const t = useT()
  const { createDialog } = useDialog()

  const handleSuccess = useCallback(
    (plotData) => {
      handleClose()
      onSuccess(plotData)
    },
    [handleClose, onSuccess]
  )

  const onError = useCallback(
    () => {
      setLoading(false)
      createDialog({
        id: 'error-map-import',
        Component: ErrorModal,
        props: {
          title: `${ t('could not {action} the {this}', {
            howMany: 1,
            gender: 'male',
            action: toLower(t('import')),
            this: toLower(t(type, { howMany: 1 }))
          })  }.`,
          message: t('your file is not well formatted')
        }
      })
    },
    [createDialog, t, type]
  )

  const getCoordinates = useCallback(
    parsedXml =>
      parsedXml
        .children.find(c => c.name === 'Document')
        .children.find(c => c.name === 'Folder')
        .children.find(c => c.name === 'Placemark')
        .children.find(c => c.name === 'Polygon')
        .children.find(c => c.name === 'outerBoundaryIs')
        .children.find(c => c.name === 'LinearRing')
        .children.find(c => c.name === 'coordinates')
        .value.split(' ').map(vals => {
          const [long, lat] = vals.split(',')
          return { lat, long }
        }),
    []
  )

  const handleFileRead = useCallback(evt => {
    try {
      const readerData = evt.target.result

      const parser = new DOMParser()
      const xml = parser.parseFromString(readerData, 'text/xml')

      const parsedXml = new XMLParser().parseFromString(
        new XMLSerializer().serializeToString(xml.documentElement)
      )

      const plotData = getCoordinates(parsedXml)

      handleSuccess({ coordinates: plotData })
      setLoading(false)
    } catch(err) {
      onError(err)
    }
  }, [getCoordinates, handleSuccess, onError])

  const handleConfirm = useCallback(
    () => {
      setLoading(true)
      const reader = new FileReader()

      reader.readAsText(file)

      reader.onloadend = handleFileRead
    },
    [file, handleFileRead]
  )

  const onChange = useCallback(
    (fileData) => setFile(fileData),
    []
  )

  return (
    <Modal
      id={ id }
      open={ open }
      loading={ loading }
      title={ t('import {this}', {
        this: t(type, { howMany: 1 })
      }) }
      maxWidth="xs"
      fullWidth
      FooterComponent={ () => (
        <>
          <Button
            id="import"
            onClick={ handleConfirm }
          >
            <I18n>import</I18n>
          </Button>
        </>
      ) }
    >
      <InputFile
        accept=".kml"
        value={ file }
        onChange={ onChange }
        label={ t('select the {this}', {
          howMany: 1,
          gender: 'male',
          this: t('file', { howMany: 1 })
        }) }
        detached // important to use it outside form
        fullWidth
      />
    </Modal>
  )
}

ImportMapModal.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
}

ImportMapModal.defaultProps = {
  type: 'map'
}

export default ImportMapModal
